import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { site } from "@/lib/site";
import { services } from "@/lib/services-data";
import { submitContact } from "@/lib/api/intake.functions";
import { Disclaimer } from "@/components/site/Disclaimer";
import {
  NonReservedNotice,
  NON_RESERVED_STATEMENT,
  NON_RESERVED_ACKNOWLEDGEMENT,
} from "@/components/site/NonReservedNotice";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Us | Nkadimeng Danny Legal & Compliance Support" },
      {
        name: "description",
        content:
          "Get in touch with Nkadimeng Danny Legal & Compliance Support. Phone, email, WhatsApp or send us a message — we'll respond promptly.",
      },
      { property: "og:title", content: "Contact Nkadimeng Danny Legal" },
      {
        property: "og:description",
        content: "Phone, email and WhatsApp — we respond quickly.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const send = useServerFn(submitContact);
  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <h1 className="font-display text-4xl text-navy md:text-5xl">
            Contact us
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            We respond to enquiries quickly. Pick the channel that works best
            for you.
          </p>
          <div className="mt-6 max-w-3xl">
            <Disclaimer variant="compact" />
          </div>
        </div>
      </section>

      <section className="container-narrow grid gap-10 py-16 md:grid-cols-[1fr_1.4fr]">
        <div className="space-y-4">
          <ContactCard
            icon={Phone}
            title="Call us"
            value={site.phone}
            href={`tel:${site.phoneIntl}`}
          />
          <ContactCard
            icon={MessageCircle}
            title="WhatsApp"
            value="Chat with us instantly"
            href={`https://wa.me/${site.whatsapp}`}
          />
          <ContactCard
            icon={Mail}
            title="Email"
            value={site.email}
            href={`mailto:${site.email}`}
          />
          <ContactCard
            icon={MapPin}
            title="Service area"
            value="Throughout South Africa"
          />
        </div>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!acknowledged) {
              toast.error(
                "Please confirm you understand our non-reserved advice scope.",
              );
              return;
            }
            const f = new FormData(e.currentTarget);
            setLoading(true);
            try {
              await send({
                data: {
                  full_name: String(f.get("full_name") || ""),
                  email: String(f.get("email") || ""),
                  phone: String(f.get("phone") || ""),
                  service: String(f.get("service") || ""),
                  message: String(f.get("message") || ""),
                },
              });
              toast.success("Message sent. We'll be in touch shortly.");
              (e.target as HTMLFormElement).reset();
              setAcknowledged(false);
            } catch (err) {
              toast.error(
                err instanceof Error ? err.message : "Could not send message",
              );
            } finally {
              setLoading(false);
            }
          }}
          className="rounded-2xl border border-border bg-card p-6 shadow-soft md:p-8"
        >
          <h2 className="font-display text-2xl text-navy">Send us a message</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Field name="full_name" label="Full name" required />
            <Field name="email" type="email" label="Email" required />
            <Field name="phone" label="Phone" />
            <SelectField
              name="service"
              label="Service"
              options={[
                { value: "", label: "Select a service" },
                ...services.map((s) => ({ value: s.slug, label: s.title })),
                { value: "other", label: "Other" },
              ]}
            />
          </div>
          <div className="mt-4">
            <label htmlFor="contact-message" className="text-sm font-medium text-navy">Message <span className="text-destructive">*</span></label>
            <textarea
              id="contact-message"
              name="message"
              required
              rows={5}
              maxLength={1000}
              className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2.5 text-base md:text-sm focus:border-gold focus:outline-none"
              placeholder="Tell us how we can help..."
            />
          </div>
          <div className="mt-6">
            <NonReservedNotice />
          </div>
          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-gold/40 bg-gold/5 p-4 text-sm leading-relaxed text-foreground">
            <input
              type="checkbox"
              required
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--navy))]"
            />
            <span>{NON_RESERVED_ACKNOWLEDGEMENT}</span>
          </label>
          <button
            type="submit"
            disabled={loading || !acknowledged}
            className="mt-6 inline-flex w-full md:w-auto items-center justify-center rounded-md bg-navy px-6 py-3 text-base md:text-sm font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
          >
            {loading ? "Sending..." : "Send message"}
          </button>
          <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
            By sending this enquiry you confirm you understand that{" "}
            {NON_RESERVED_STATEMENT}
          </p>
        </form>
      </section>
    </>
  );
}

function ContactCard({
  icon: Icon,
  title,
  value,
  href,
}: {
  icon: typeof Phone;
  title: string;
  value: string;
  href?: string;
}) {
  const content = (
    <div className="flex items-start gap-4 rounded-xl border border-border bg-card p-5 shadow-soft transition hover:shadow-lifted">
      <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          {title}
        </div>
        <div className="font-medium text-navy">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
      {content}
    </a>
  ) : (
    content
  );
}

function Field({
  name,
  label,
  type = "text",
  required,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
}) {
  const inputMode =
    type === "email" ? "email" : name === "phone" || type === "tel" ? "tel" : undefined;
  const autoComplete =
    name === "full_name"
      ? "name"
      : type === "email"
        ? "email"
        : name === "phone" || type === "tel"
          ? "tel"
          : undefined;
  return (
    <div>
      <label htmlFor={`contact-${name}`} className="text-sm font-medium text-navy">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <input
        id={`contact-${name}`}
        name={name}
        type={name === "phone" ? "tel" : type}
        required={required}
        inputMode={inputMode as any}
        autoComplete={autoComplete}
        maxLength={type === "email" ? 255 : 100}
        className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2.5 text-base md:text-sm focus:border-gold focus:outline-none"
      />
    </div>
  );
}

function SelectField({
  name,
  label,
  options,
}: {
  name: string;
  label: string;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label htmlFor={`contact-${name}`} className="text-sm font-medium text-navy">{label}</label>
      <select
        id={`contact-${name}`}
        name={name}
        className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2.5 text-base md:text-sm focus:border-gold focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
